import { formatLocalDate } from '../utils/localDateTime';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { VcmcDashboardState, VcmcUpdate, VcmcAfterEventReport, VcmcEmergencyTool } from '@types';
import { getVcmcUpdate, generateVcmcAfterEventReport } from '@common/services/geminiService';
import { ICONS, RISK_LEVEL_COLORS } from './constants';
import UnifiedMap from './UnifiedMap';
import useGoogleMaps from './useGoogleMaps';

interface VCMCProps {
    initialState: VcmcDashboardState;
    onExit: () => void;
}

const KpiCard: React.FC<{ title: string; value: string; trend: 'up' | 'down' | 'stable'; onClick: () => void }> = ({ title, value, trend, onClick }) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            setIsAnimating(true);
            const timer = setTimeout(() => setIsAnimating(false), 800);
            return () => clearTimeout(timer);
        }
    }, [value, trend]);

    const trendIcon = {
        up: <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>,
        down: <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17l5-5m0 0l-5-5m5 5H6" /></svg>,
        stable: <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>,
    }[trend];
    
    return (
        <button type="button" onClick={onClick} className="glass-panel p-4 flex justify-between items-center relative overflow-hidden group text-left w-full hover:border-blue-500/60 focus:outline-none focus:ring-2 focus:ring-blue-500">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative z-10">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{title}</p>
                <p className="text-2xl font-black text-white tracking-tight">{value}</p>
            </div>
            <div className={`relative z-10 p-2 rounded-full bg-slate-800/50 border border-slate-700/50 shadow-inner ${isAnimating ? 'animate-pulse-icon ring-2 ring-blue-500/50' : ''}`}>
                {trendIcon}
            </div>
        </button>
    );
};

const VCMC: React.FC<VCMCProps> = ({ initialState, onExit }) => {
    const { isLoaded } = useGoogleMaps();
    const [state, setState] = useState<VcmcDashboardState>(initialState);
    const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [userAction, setUserAction] = useState('');
    const [isSubmittingAction, setIsSubmittingAction] = useState(false);
    const [afterEventReport, setAfterEventReport] = useState<VcmcAfterEventReport | null>(null);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [operationError, setOperationError] = useState('');
    const [selectedDetail, setSelectedDetail] = useState<{ title: string; lines: string[] } | null>(null);
    
    const eventFeedRef = useRef<HTMLDivElement>(null);
    const newsFeedRef = useRef<HTMLDivElement>(null);

    const applyUpdate = (prevState: VcmcDashboardState, update: VcmcUpdate): VcmcDashboardState => {
        const newState = { ...prevState };
        
        if (update.new_event) {
            newState.liveEvents = [{ ...update.new_event, acknowledged: false }, ...(prevState.liveEvents || [])];
        }
        
        if (update.new_news_item) {
            newState.newsFeeds = [update.new_news_item, ...(prevState.newsFeeds || [])];
        }

        if (update.updated_threat_level) {
            newState.globalThreatLevel = update.updated_threat_level;
        }

        if (update.task_update) {
            newState.activeTasks = (prevState.activeTasks || []).map(task =>
                task.id === update.task_update?.id ? { ...task, status: update.task_update.status } : task
            );
        }

        if (update.kpi_update) {
            newState.kpis = (prevState.kpis || []).map(kpi =>
                kpi.title === update.kpi_update?.title ? { ...kpi, ...update.kpi_update } : kpi
            );
        }

        if (update.resource_update) {
            newState.resources = (prevState.resources || []).map(res =>
                res.id === update.resource_update?.id ? { ...res, status: update.resource_update.status, location: update.resource_update.location || res.location } : res
            );
        }

        if (update.new_action_result) {
            newState.actionLog = [update.new_action_result, ...(prevState.actionLog || [])];
            // Add action result as a live event too
            newState.liveEvents = [{
                id: `act-${Date.now()}`,
                time: new Date().toLocaleTimeString(),
                description: `ACTION RESPONSE: ${update.new_action_result.result}`,
                type: 'Action',
                acknowledged: false
            }, ...(newState.liveEvents || [])];
        }

        return newState;
    };

    const fetchUpdate = useCallback(async () => {
        if (isPaused || isSubmittingAction || isGeneratingReport) return;
        setIsLoadingUpdate(true);
        try {
            const update = await getVcmcUpdate(state);
            setState(prevState => applyUpdate(prevState, update));
        } catch (e) {
            console.error("Failed to get VCMC update:", e);
        } finally {
            setIsLoadingUpdate(false);
        }
    }, [state, isPaused, isSubmittingAction, isGeneratingReport]);

    useEffect(() => {
        const interval = setInterval(fetchUpdate, 30000); // Slower interval for auto-ticks
        return () => clearInterval(interval);
    }, [fetchUpdate]);

    const handleActionSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!userAction.trim() || isSubmittingAction) return;

        setIsSubmittingAction(true);
        try {
            setOperationError('');
            const update = await getVcmcUpdate(state, userAction);
            setState(prevState => applyUpdate(prevState, update));
            setUserAction('');
        } catch (e) {
            console.error("Failed to submit action:", e);
            setOperationError('The simulation action failed. Please retry.');
        } finally {
            setIsSubmittingAction(false);
        }
    };

    const handleToolLaunch = async (tool: VcmcEmergencyTool) => {
        if (isSubmittingAction || tool.status === 'Offline')
            return;

        setIsSubmittingAction(true);
        setOperationError('');
        setSelectedDetail({ title: tool.name, lines: ['Launching critical tool...', `Category: ${tool.category}`] });
        setState(prev => ({
            ...prev,
            emergencyTools: (prev.emergencyTools || []).map(item => item.id === tool.id ? { ...item, status: 'Active' } : item)
        }));

        try {
            const directive = `Activate critical tool "${tool.name}" in category "${tool.category}". Assess its operational effect and return a concrete simulation result.`;
            const update = await getVcmcUpdate(state, directive);
            setState(prev => {
                const updated = applyUpdate(prev, update);
                return {
                    ...updated,
                    emergencyTools: (updated.emergencyTools || []).map(item => item.id === tool.id ? { ...item, status: 'Ready' } : item)
                };
            });
            setSelectedDetail({
                title: tool.name,
                lines: [update.new_action_result?.result || 'Tool launched successfully.', `Category: ${tool.category}`]
            });
        } catch (e) {
            console.error('Failed to launch VCMC tool:', e);
            setState(prev => ({
                ...prev,
                emergencyTools: (prev.emergencyTools || []).map(item => item.id === tool.id ? { ...item, status: 'Ready' } : item)
            }));
            setOperationError(`${tool.name} failed to launch. Please retry.`);
            setSelectedDetail({ title: tool.name, lines: ['Launch failed. Please retry.', `Category: ${tool.category}`] });
        } finally {
            setIsSubmittingAction(false);
        }
    };

    const handleTerminate = async () => {
        setIsGeneratingReport(true);
        try {
            const report = await generateVcmcAfterEventReport(state);
            setAfterEventReport(report);
        } catch (e) {
            console.error("Failed to generate AER:", e);
            onExit(); // Fallback to immediate exit
        } finally {
            setIsGeneratingReport(false);
        }
    };

    const handleAcknowledge = (eventId: string) => {
        setState(prev => ({
            ...prev,
            liveEvents: (prev.liveEvents || []).map(event => event.id === eventId ? { ...event, acknowledged: true } : event)
        }));
    };

    const validGeospatialThreats = state.geospatialThreats?.filter(
        t => t && typeof t.lat === 'number' && typeof t.lng === 'number'
    );
    const hasValidMapCenter = state.mapCenter && typeof state.mapCenter.lat === 'number' && typeof state.mapCenter.lng === 'number';

    if (afterEventReport) {
        return (
            <div className="bg-[#0B1121] text-white p-8 rounded-xl border border-slate-800 shadow-2xl h-full overflow-y-auto custom-scrollbar animate-fade-in">
                <div className="max-w-4xl mx-auto">
                    <div className="flex justify-between items-center mb-8 border-b border-slate-800 pb-6">
                        <div>
                            <h1 className="text-4xl font-black tracking-tight text-white mb-2">AFTER-EVENT REPORT</h1>
                            <p className="text-slate-400 font-mono tracking-widest uppercase text-sm">Simulation Final Resolution • {formatLocalDate(afterEventReport.completionDate, afterEventReport.completionDate || '')}</p>
                        </div>
                        <button onClick={onExit} className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded shadow-lg transition-all">Close Simulation</button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                        <div className="glass-panel p-6 border-l-4 border-blue-500">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Executive Summary</h3>
                            <p className="text-slate-300 leading-relaxed italic">"{afterEventReport.executiveSummary}"</p>
                        </div>
                        <div className="glass-panel p-6 border-l-4 border-emerald-500">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Strategic Outcome</h3>
                            <p className="text-slate-200 font-bold text-xl">{afterEventReport.outcome}</p>
                            <p className="text-slate-400 text-sm mt-2 leading-relaxed">{afterEventReport.resolutionDetails}</p>
                        </div>
                    </div>

                    <div className="space-y-8 mb-12">
                        <section>
                            <h3 className="text-sm font-black text-white uppercase tracking-widest mb-4 flex items-center">
                                <span className="w-8 h-px bg-blue-500 mr-3" /> Team Performance Assessment
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {Object.entries(afterEventReport.performanceMetrics).map(([key, value]) => (
                                    <div key={key} className="bg-slate-800/50 p-4 rounded border border-slate-700/50">
                                        <p className="text-[10px] text-slate-500 uppercase font-bold tracking-tighter mb-1">{key.replace(/([A-Z])/g, ' $1')}</p>
                                        <p className="text-2xl font-black text-white">{value}/100</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section>
                            <h3 className="text-sm font-black text-white uppercase tracking-widest mb-4 flex items-center">
                                <span className="w-8 h-px bg-red-500 mr-3" /> Critical Capability Gaps
                            </h3>
                            <div className="space-y-3">
                                {afterEventReport.lessonsLearned.map((lesson, idx) => (
                                    <div key={idx} className="bg-red-500/5 border border-red-500/20 p-4 rounded flex items-start gap-4">
                                        <span className="text-red-400 font-black text-lg">!</span>
                                        <p className="text-slate-300 italic">"{lesson}"</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section>
                            <h3 className="text-sm font-black text-white uppercase tracking-widest mb-4 flex items-center">
                                <span className="w-8 h-px bg-emerald-500 mr-3" /> Strategic Recommendations
                            </h3>
                            <div className="space-y-3">
                                {afterEventReport.recommendations.map((rec, idx) => (
                                    <div key={idx} className="bg-emerald-500/5 border border-emerald-500/20 p-4 rounded flex items-start gap-4">
                                        <span className="text-emerald-400 font-black text-lg">✓</span>
                                        <p className="text-slate-300">{rec}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>

                    <div className="text-center text-slate-500 text-[10px] uppercase font-mono tracking-[0.2em] border-t border-slate-800 pt-8">
                        End of Simulation Intelligence Report • IntelOptics VCMC Engine v3.0
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#0B1121] text-white p-4 h-full flex flex-col relative overflow-hidden font-sans">
            {/* Background Glow */}
            <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] opacity-10 blur-[120px] pointer-events-none transition-colors duration-1000 ${
                state.globalThreatLevel === 'Critical' ? 'bg-red-600' : 'bg-blue-600'
            }`} />

            {/* Header */}
            <div className="flex justify-between items-center mb-4 flex-shrink-0 z-20 glass-panel p-3 border-slate-700/50">
                <div className="flex items-center">
                    <div className="p-1.5 bg-slate-800/80 rounded border border-slate-700/50 mr-3">
                        {ICONS.logo}
                    </div>
                    <div>
                        <h1 className="text-lg font-black tracking-tighter text-white uppercase">{state.crisisTitle}</h1>
                        <div className="flex items-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse mr-2" />
                            <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest leading-none">Simulation Tactical Hub</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <div className={`px-3 py-1 font-black uppercase tracking-widest text-[10px] rounded border ${
                        state.globalThreatLevel === 'Critical' ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                    }`}>
                        Defcon: {state.globalThreatLevel}
                    </div>
                    <button onClick={() => setIsPaused(!isPaused)} className="p-1.5 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700">
                        {isPaused ? <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path d="M4.5 3a.5.5 0 00-.5.5v13a.5.5 0 00.854.354l7-6.5a.5.5 0 000-.708l-7-6.5A.5.5 0 004.5 3z"/></svg> : <svg className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"/></svg>}
                    </button>
                    <button 
                        onClick={handleTerminate} 
                        disabled={isGeneratingReport}
                        className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest bg-red-600 hover:bg-red-500 text-white rounded transition-all disabled:opacity-50"
                    >
                        {isGeneratingReport ? 'Finalizing...' : 'Terminate Session'}
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="grid grid-cols-12 gap-4 flex-grow min-h-0 z-10">
                {/* Column 1: Intelligence Feed & News */}
                <div className="col-span-12 lg:col-span-3 flex flex-col gap-4 min-h-0">
                    {/* Live Intelligence */}
                    <div className="glass-panel flex flex-col h-1/2 min-h-0 border-slate-800 pb-0">
                        <div className="p-3 border-b border-slate-800/50 flex items-center">
                            <span className="mr-2 text-red-500">{ICONS.feed}</span>
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global Intelligence Feed</h3>
                        </div>
                        <div className="flex-grow overflow-y-auto p-3 space-y-3 custom-scrollbar">
                            {(state.liveEvents || []).map(event => (
                                <div key={event.id} className={`p-2.5 rounded border transition-all ${event.acknowledged ? 'bg-slate-800/30 border-slate-700/30' : 'bg-red-500/5 border-red-500/30 shadow-[0_0_10px_rgba(239,68,68,0.1)]'}`}>
                                    <div className="flex justify-between items-start mb-1">
                                        <span className={`text-[9px] font-mono tracking-tighter ${event.acknowledged ? 'text-slate-600' : 'text-red-400 font-bold underline'}`}>{event.time}</span>
                                        {!event.acknowledged && (
                                            <button onClick={() => handleAcknowledge(event.id)} className="text-[8px] font-bold uppercase tracking-tight text-white bg-red-600 px-1.5 py-0.5 rounded leading-none">Ack</button>
                                        )}
                                    </div>
                                    <p className={`text-xs leading-snug ${event.acknowledged ? 'text-slate-400' : 'text-white'}`}>{event.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* News Feed */}
                    <div className="glass-panel flex flex-col h-1/2 min-h-0 border-slate-800 pt-0">
                        <div className="p-3 border-b border-slate-800/50 flex items-center">
                            <span className="mr-2 text-blue-500">{ICONS.news_globe}</span>
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global Media 24/7</h3>
                        </div>
                        <div className="flex-grow overflow-y-auto p-3 space-y-4 custom-scrollbar">
                            {(state.newsFeeds || []).map((news, idx) => (
                                <div key={idx} className="border-l-2 border-slate-700 pl-3">
                                    <p className="text-[9px] font-bold text-blue-400 uppercase tracking-widest mb-1">{news.source} • {news.time}</p>
                                    <h4 className="text-xs font-bold text-white mb-1 leading-tight">{news.headline}</h4>
                                    <p className="text-[10px] text-slate-400 leading-normal line-clamp-2">{news.content}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Column 2: Map & Action Input */}
                <div className="col-span-12 lg:col-span-6 flex flex-col gap-4 min-h-0">
                    {/* KPIS Top Bar */}
                    <div className="grid grid-cols-3 gap-3">
                        {(state.kpis || []).map(kpi => (
                            <KpiCard
                                key={kpi.title}
                                {...kpi}
                                onClick={() => setSelectedDetail({
                                    title: kpi.title,
                                    lines: [`Current value: ${kpi.value}`, `Trend: ${kpi.trend}`]
                                })}
                            />
                        ))}
                    </div>

                    {/* Integrated Map */}
                    <div className="flex-grow glass-panel p-0 overflow-hidden relative border-slate-700/50">
                         {hasValidMapCenter ? (
                            <UnifiedMap
                                initialViewState={{
                                    longitude: state.mapCenter!.lng,
                                    latitude: state.mapCenter!.lat,
                                    zoom: 4
                                }}
                                theme="dark"
                                geospatialThreats={validGeospatialThreats}
                                isLoaded={isLoaded}
                            />
                        ) : <div className="flex items-center justify-center h-full text-slate-500 uppercase font-bold text-xs tracking-widest">Initializing Map...</div>}
                    </div>

                    {/* Interactive Action Center */}
                    <div className="glass-panel p-4 border-t-2 border-blue-500/50">
                        <div className="flex items-center justify-between mb-3 border-b border-slate-800 pb-2">
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center">
                                <span className="mr-2 text-emerald-500">{ICONS.send}</span> Executive Action Center
                            </h3>
                            {isSubmittingAction && (
                                <div className="flex items-center">
                                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping mr-2" />
                                    <span className="text-[9px] font-bold text-emerald-400 uppercase">Processing...</span>
                                </div>
                            )}
                        </div>
                        <form onSubmit={handleActionSubmit} className="relative">
                            <textarea
                                value={userAction}
                                onChange={(e) => setUserAction(e.target.value)}
                                placeholder="ISSUE SUB-DIRECTIVES... (e.g. 'Deploy mobile response unit to Sector 7', 'Evacuate regional office')"
                                className="w-full bg-slate-900/80 border border-slate-700 rounded p-3 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-blue-500 transition-colors custom-scrollbar h-20 resize-none font-mono"
                                disabled={isSubmittingAction}
                            />
                            <button 
                                type="submit"
                                disabled={isSubmittingAction || !userAction.trim()}
                                className="absolute bottom-3 right-3 p-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg shadow-lg transition-transform active:scale-95 disabled:bg-slate-700 disabled:opacity-50"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M13 5l7 7-7 7M5 5l7 7-7 7"/></svg>
                            </button>
                        </form>
                        {operationError && (
                            <p role="alert" className="mt-2 text-[10px] font-semibold text-red-400">{operationError}</p>
                        )}
                    </div>
                </div>

                {/* Column 3: Logistics & Tools */}
                <div className="col-span-12 lg:col-span-3 flex flex-col gap-4 min-h-0">
                    {/* Tactical Tasks */}
                    <div className="glass-panel flex flex-col h-[35%] min-h-0">
                        <div className="p-3 border-b border-slate-800/50 flex items-center">
                            <span className="mr-2 text-blue-400">{ICONS.tasks}</span>
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tactical Status</h3>
                        </div>
                        <div className="flex-grow overflow-y-auto p-3 space-y-2 custom-scrollbar">
                            {(state.activeTasks || []).map(task => (
                                <button
                                    type="button"
                                    key={task.id}
                                    onClick={() => setSelectedDetail({ title: 'Tactical Task', lines: [task.description, `Status: ${task.status}`] })}
                                    className="w-full bg-slate-800/50 hover:bg-slate-700/70 p-2 rounded border border-slate-700/30 flex justify-between items-center group text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <div className="flex items-center gap-2">
                                        <div className={`w-1.5 h-1.5 rounded-full ${
                                            task.status === 'Completed' ? 'bg-emerald-500' : task.status === 'Delayed' ? 'bg-red-500' : 'bg-blue-500'
                                        }`} />
                                        <p className="text-[10px] font-medium text-slate-300 leading-tight">{task.description}</p>
                                    </div>
                                    <span className={`text-[8px] font-bold uppercase tracking-tighter px-1.5 py-0.5 rounded ${
                                        task.status === 'Completed' ? 'text-emerald-400 bg-emerald-400/10' : task.status === 'Delayed' ? 'text-red-400 bg-red-400/10' : 'text-blue-400 bg-blue-400/10'
                                    }`}>{task.status}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Resources */}
                    <div className="glass-panel flex flex-col h-[35%] min-h-0">
                        <div className="p-3 border-b border-slate-800/50 flex items-center">
                            <span className="mr-2 text-emerald-400">{ICONS.briefcase}</span>
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Logistics & Resources</h3>
                        </div>
                        <div className="flex-grow overflow-y-auto p-3 space-y-2 custom-scrollbar">
                            {(state.resources || []).map(resource => (
                                <button
                                    type="button"
                                    key={resource.id}
                                    onClick={() => setSelectedDetail({
                                        title: resource.name,
                                        lines: [`Type: ${resource.type}`, `Status: ${resource.status}`, `Position: ${resource.location}`]
                                    })}
                                    className="w-full bg-slate-800/30 hover:bg-slate-700/60 p-2 rounded border border-slate-700/30 text-left focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                >
                                    <div className="flex justify-between items-center mb-1">
                                        <p className="text-[10px] font-black text-white">{resource.name}</p>
                                        <span className={`text-[8px] font-bold uppercase px-1 rounded ${
                                            resource.status === 'Available' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                                        }`}>{resource.status}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <p className="text-[9px] text-slate-500 font-mono">{resource.type}</p>
                                        <p className="text-[9px] text-slate-400 italic">Pos: {resource.location}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Emergency Tools */}
                    <div className="glass-panel flex flex-col h-[30%] min-h-0">
                        <div className="p-3 border-b border-slate-800/50 flex items-center">
                            <span className="mr-2 text-yellow-500">{ICONS.emergency_tool}</span>
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Critical Tooling</h3>
                        </div>
                        <div className="flex-grow overflow-y-auto p-3 grid grid-cols-2 gap-2 custom-scrollbar">
                            {(state.emergencyTools || []).map(tool => (
                                <button
                                    type="button"
                                    key={tool.id}
                                    onClick={() => handleToolLaunch(tool)}
                                    disabled={isSubmittingAction || tool.status === 'Offline'}
                                    className="p-2 bg-slate-800/80 hover:bg-slate-700 border border-slate-700 rounded text-left transition-all group overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                >
                                    <p className="text-[9px] font-bold text-white mb-0.5 leading-none">{tool.name}</p>
                                    <p className="text-[8px] text-slate-500 uppercase font-black group-hover:text-yellow-400 transition-colors">
                                        {tool.status === 'Active' ? 'Launching...' : tool.status === 'Offline' ? 'Offline' : 'Launch Tool'}
                                    </p>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {selectedDetail && (
                <div className="absolute inset-0 z-50 bg-slate-950/75 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelectedDetail(null)}>
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="vcmc-detail-title"
                        className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl"
                        onClick={event => event.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <h3 id="vcmc-detail-title" className="text-sm font-black text-white uppercase tracking-wider">{selectedDetail.title}</h3>
                            <button type="button" onClick={() => setSelectedDetail(null)} className="text-slate-400 hover:text-white" aria-label="Close details">×</button>
                        </div>
                        <div className="space-y-2">
                            {selectedDetail.lines.map((line, index) => (
                                <p key={index} className="text-sm text-slate-300 leading-relaxed">{line}</p>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Tactical Footer Overlay */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-50 z-30" />
        </div>
    );
};

export default VCMC;
